import { createContext, FC, FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { promiseWithResolvers } from "~/utils/promiseWithResolvers";


interface LocalMessageSectionProps {
  applyForAddress: (id?:string) => { address:string, unregisterAddr:UnsubscribeHandle };
  findRoutesAndDeliver: (m: LocalMessageA, toAddress?: string | { relativeAddress: RelativeAddrs}) => void;
  subscribeFromAddress: (address: string, filters: LocalMessageFilter[], handler: (m: LocalMessageA)=>void) => UnsubscribeHandle;
}
interface UnsubscribeHandle {
  (): void
}

export interface LocalMessageSubPub {
  subscribe: (filters: LocalMessageFilter[], handler: (m: LocalMessageA)=>void) => UnsubscribeHandle;
  publish: (m: LocalMessage, toAddress?: string|{ relativeAddress: RelativeAddrs}) => void;
  // Filters: typeof FilterHelper,
}
interface LocalMessagingContextProps extends LocalMessageSubPub {
  // id: string;
  address: string;
}

const LocalMessagingSectionContext = createContext<LocalMessageSectionProps>({} as any)
const LocalMessagingContext = createContext<LocalMessagingContextProps>({} as any)

interface LocalMessage {
  topic: string;
  payload: any;
}
interface LocalMessageA extends LocalMessage {
  fromAddress: string;
}

type LocalMessageFilter =
| LightLocalMessageFilter
| CustomLocalMessageFilter
;
interface LightLocalMessageFilter {
  topic?: string;
  topicPrefix?: string;
  exactAddress?: string;
  relativeAddress?: RelativeAddrs;
}
type CustomLocalMessageFilter =
  [string, (x:LocalMessageA) => boolean]

enum RelativeAddrs {
  None = 'NONE',
  All = 'ALL',
  Parent = 'PARENT',
  Ancestors = 'ANCESTORS',
  Children = 'CHILDREN',
  Desendents = 'DESENDENTS',
}


export const FilterHelper = Object.freeze({
  withTopic: (s:string): LightLocalMessageFilter => ({ topic: s }),
  withTopicPrefix: (s:string): LightLocalMessageFilter => ({ topicPrefix: s }),
  withExactAddress: (s:string): LightLocalMessageFilter => ({ exactAddress: s }),
  withRelativeAddress: Object.freeze(Object.keys(RelativeAddrs).reduce((acc, k) => {
    acc[k] = Object.freeze({ relativeAddress: (RelativeAddrs as any)[k] })
    return acc
  }, {} as any)) as { [K in keyof typeof RelativeAddrs]: { relativeAddress: RelativeAddrs} },
  withCustom: (name: string, fn:(x:LocalMessageA) => boolean) => [name, fn] as CustomLocalMessageFilter,
})


const rootAddr = '$.' // '$.' stands for root itself
const getParentAddr = (s:string) => {
  const i = s.lastIndexOf('.', s.length - 2)
  return s.substring(0, i + 1)
}
const charCodes = '1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ`~!@#$%^&*()-_=+[]{};:\'"\\|<>,./?'
const randomInRange = (n:number) => {
  const l = Math.floor(n)
  const rndMsg = Math.floor(Math.random() * 1000) % l
  return rndMsg
}
const generateRandomSubsectionEntry = () => {
  const charNum = randomInRange(6) + 1
  return [...new Array(charNum)].map(() => charCodes[randomInRange(charCodes.length)]).join('')
}


interface SubscriptionStub {
  address: string;
  // topic: string;
  filters: LocalMessageFilter[];
  signature: string;
  handler: (m: LocalMessageA)=>void;
}

interface SubscriptionBook {
  [address: string]: SubscriptionStub[];
}

interface LocalMessagingRootInfo extends LocalMessageSubPub {
  LocalMessagingRootHost: FunctionComponent;
}

let Logger : typeof console = {
  debug(message?: any, ...optionalParams: any[]) {},
  error(message?: any, ...optionalParams: any[]) {},
  info(message?: any, ...optionalParams: any[]) {},
  log(message?: any, ...optionalParams: any[]) {},
} as any
// !! remember to comment out the below line !!
// Logger = console;
const LogSwitch = {
  Subscribe: false,
  Publish: false,
  ApplyAddress: false,
  TapMsg: false,
}

export const useLocalMessagingRoot = (): LocalMessagingRootInfo => {
  const { current: addrBook } = useRef({
    orderedAddress: [rootAddr] as string[],
    subscrBook: {} as SubscriptionBook,
  })
  const { current: subsectionBook } = useRef({
    registeredSubsections: new Set<string>()
  })

  const {sectionProps, ctxProps, LocalMessagingRootHost} = useMemo(() => {
    const sectionProps = ({
      findRoutesAndDeliver: (m: LocalMessageA, toAddress?: string|{ relativeAddress: RelativeAddrs}) => {
        const frozenMsg = Object.freeze({
          topic: m.topic,
          fromAddress: m.fromAddress,
          payload: Object.freeze(m.payload),
        })
        LogSwitch.Publish && Logger.log('Local Message recieved', frozenMsg)

        let stubs: SubscriptionStub[] = []
        const allAddrs = addrBook.orderedAddress.filter(addr => addr !== m.fromAddress)
        if (toAddress != null) {
          if (typeof(toAddress) === 'string') {
            if (allAddrs.indexOf(toAddress) > -1) {
              stubs = addrBook.subscrBook[toAddress]
            }
          } else {
            const rAddr = toAddress.relativeAddress
            if (RelativeAddrs.None === rAddr) {
              stubs = []
            } else if (RelativeAddrs.All === rAddr) {
              stubs = allAddrs.flatMap(addr => addrBook.subscrBook[addr])
            } else if (RelativeAddrs.Ancestors === rAddr) {
              stubs = 
                allAddrs.filter(addr => m.fromAddress.startsWith(addr))
                .flatMap(addr => addrBook.subscrBook[addr])
            } else if (RelativeAddrs.Parent === rAddr) {
              const pAddr = getParentAddr(m.fromAddress)
              stubs = addrBook.subscrBook[pAddr] ?? []
            } else if (RelativeAddrs.Children === rAddr) {
              stubs = 
                allAddrs.filter(addr => addr.length > m.fromAddress.length && getParentAddr(addr) === m.fromAddress)
                .flatMap(addr => addrBook.subscrBook[addr])
            } else if (RelativeAddrs.Desendents === rAddr) {
              stubs = 
                allAddrs.filter(addr => addr.startsWith(m.fromAddress))
                .flatMap(addr => addrBook.subscrBook[addr])
            }
          }
        } else {
          stubs = allAddrs.flatMap(addr => addrBook.subscrBook[addr])
        }
        const routesFound = stubs.filter(stub => {
          if (!stub.filters) { return true }
          return stub.filters.every(f => {
            if (f instanceof Array) {
              return f[1](m)
            } else {
              if (f.relativeAddress === RelativeAddrs.None) { return false }
              if (f.topic) { 
                if (f.topic !== m.topic) { return false }
              } else if (f.topicPrefix) {
                if (!m.topic.startsWith(f.topicPrefix)) { return false }
              }
    
              if (f.exactAddress) {
                if (f.exactAddress !== m.fromAddress) { return false }
              } else if (f.relativeAddress) {
                return (
                  f.relativeAddress === RelativeAddrs.All ? true
                : f.relativeAddress === RelativeAddrs.Ancestors  ? stub.address.startsWith(m.fromAddress)
                : f.relativeAddress === RelativeAddrs.Desendents ? m.fromAddress.startsWith(stub.address)
                : f.relativeAddress === RelativeAddrs.Parent     ? m.fromAddress.length < stub.address.length && m.fromAddress === getParentAddr(stub.address)
                : f.relativeAddress === RelativeAddrs.Children   ? m.fromAddress.length > stub.address.length && getParentAddr(m.fromAddress) === stub.address
                : false)
              }
              return true
            }
          })
        })
        LogSwitch.Publish && Logger.log('Routes found for msg delivery', routesFound.map(stub => stub.address))
        const handlers = routesFound.map(stub => stub.handler)
        
        if (handlers.length > 0) {
          setTimeout(() => {
            for (let handler of handlers) {
              handler(frozenMsg)
            }
            LogSwitch.TapMsg && Logger.log(frozenMsg)
          })
        }
      },
      applyForAddress: (id?:string) => {
        let subsectionEntry = btoa(id ?? generateRandomSubsectionEntry())
        while (subsectionBook.registeredSubsections.has(subsectionEntry)) {
          subsectionEntry = btoa(generateRandomSubsectionEntry())
        }
        subsectionBook.registeredSubsections.add(subsectionEntry)
        LogSwitch.ApplyAddress && Logger.log(rootAddr, [...subsectionBook.registeredSubsections])

        const address = `${rootAddr}${subsectionEntry}.`

        LogSwitch.ApplyAddress && Logger.log('Registering address', address)
        
        const unregisterAddr = () => {
          LogSwitch.ApplyAddress && Logger.log('Unregistering address', address)
          subsectionBook.registeredSubsections.delete(subsectionEntry)
        }
        return { address, unregisterAddr }
      },
      subscribeFromAddress: (address: string, filters: LocalMessageFilter[], handler: (m: LocalMessageA)=>void) => {
        // address is empty, this is a temporal action
        if (!address || filters == null) { return () => {} }

        const mergedFilters = filters.reduce((acc, filter) => {
          if (!acc) { return acc }
          if (filter instanceof Array) {
            acc.push(filter)
          } else {
            if (filter.relativeAddress === RelativeAddrs.None) { return null }
            if (!acc[0] || (acc[0] instanceof Array)) {
              acc.unshift({})
            }
            acc[0] = {...acc[0], ...filter}
          }
          return acc
        }, [] as LocalMessageFilter[]|null)

        // when specified RelativeAddrs.None, no real subscription shall be performed.
        if (!mergedFilters) { return () => {} }
        
        if (addrBook.orderedAddress.indexOf(address) < 0) {
          const aParts = address.split('.')
          aParts.pop()
    
          const i = addrBook.orderedAddress.findIndex(s => {
            const parts = s.split('.')
            parts.pop()
            if (parts.length !== aParts.length) { return parts.length > aParts.length }
            for (let n = 0; n < aParts.length; n++)
            {
              if (parts[n] !== aParts[n]) { return parts[n] > aParts[n] }
            }
            return false
          })
          if (i > -1) {
            addrBook.orderedAddress.splice(i, 0, address)
          } else {
            addrBook.orderedAddress.push(address)
          }
        }

        const stubs = addrBook.subscrBook[address] ?? (addrBook.subscrBook[address] = [])
        const stub = { address, handler, filters: mergedFilters, signature: JSON.stringify(mergedFilters) }
        LogSwitch.Subscribe && Logger.log('subscribed!', stub.address, stub.signature)
        const iStub = stubs.findIndex(s => s.signature === stub.signature)
        if (iStub > -1) {
          stubs.splice(iStub, 1, stub)
        } else {
          stubs.push(stub)
        }

        return (() => {
          LogSwitch.Subscribe && Logger.log('unsubscribing...', address)
          const stubs = addrBook.subscrBook[address]
          if (stubs) {
            const i = stubs.indexOf(stub)
            if (i > -1) {
              stubs.splice(i, 1)
            } else {
              LogSwitch.Subscribe && Logger.warn('can not unsubscribe stub!', stub.address, stub.signature)
            }
            if (stubs.length === 0) {
              delete addrBook.subscrBook[address]
              
              const j = addrBook.orderedAddress.indexOf(address)
              if (j > -1) { addrBook.orderedAddress.splice(j, 1) }
            }
          }
          else {
            LogSwitch.Subscribe && Logger.warn('can not unsubscribe stub!', address)
          }
        })
      },
    })
    const ctxProps = {
      id: rootAddr,
      address: rootAddr,
      subscribe: (filters: LocalMessageFilter[], handler: (m: LocalMessageA)=>void) => {
        return sectionProps.subscribeFromAddress(rootAddr, filters, handler)
      },
      publish: (m: LocalMessage, toAddress?: string|{ relativeAddress: RelativeAddrs}) => {
        const m1: LocalMessageA = {...m, fromAddress: rootAddr}
        sectionProps.findRoutesAndDeliver(m1, toAddress)
      },
      Filters: FilterHelper,
    }
    const LocalMessagingRootHost : FC = ({children}) => {
      return (
        <LocalMessagingSectionContext.Provider value={sectionProps}>
          {children}
          {/* <LocalMessagingContext.Provider value={ctxProps}>
          </LocalMessagingContext.Provider> */}
        </LocalMessagingSectionContext.Provider>
      )
    }
    return {sectionProps, ctxProps, LocalMessagingRootHost}
  }, [addrBook.orderedAddress, addrBook.subscrBook, subsectionBook.registeredSubsections])

  return {LocalMessagingRootHost, ...ctxProps} 
}


export const LocalMessageLocation: FC<{id?:string}> = ({id, children}) => {
  const {applyForAddress, findRoutesAndDeliver, subscribeFromAddress} = useContext(LocalMessagingSectionContext)
  const { current: registeredSubsections } = useRef(new Set<string>())
  const [states, setStates] = useState({
    address: '',
  })
  useEffect(() => {
    const { address, unregisterAddr } = applyForAddress(id)
    setStates(v => v.address !== address ? {...v, address} : v)
    return () => {
      unregisterAddr()
    }
  }, [applyForAddress, id]) // this effect shall only run once

  const pubSub = useMemo(() => ({
    subscribe: (filters: LocalMessageFilter[], handler: (m: LocalMessageA)=>void) => {
      const qUfn = promiseWithResolvers()
      setStates(v => {
        const ufn = subscribeFromAddress(v.address, filters, handler)
        qUfn.promise.then(ufn)
        return v
      })
      return () => qUfn.resolve(1)
    },
    publish: (m: LocalMessage, toAddress?: string|{ relativeAddress: RelativeAddrs}) => {
      setStates(v => {
        if (v.address) {
          const m1: LocalMessageA = {...m, fromAddress: v.address}
          findRoutesAndDeliver(m1, toAddress)
        }
        return v
      })
    },
    Filters: FilterHelper,
  }), [findRoutesAndDeliver, subscribeFromAddress, ...[states.address]])

  const ctxProps: LocalMessagingContextProps = useMemo(() => ({ address: states.address, ...pubSub }), [states.address, pubSub])

  const sectionProps: LocalMessageSectionProps = useMemo(() => ({
    findRoutesAndDeliver, subscribeFromAddress,
    applyForAddress: (id?:string) => {
      if (!states.address) { return { address: '', unregisterAddr: ()=>{} } }

      let subsectionEntry = btoa(id ?? generateRandomSubsectionEntry())
      while (registeredSubsections.has(subsectionEntry)) {
        subsectionEntry = btoa(generateRandomSubsectionEntry())
      }
      registeredSubsections.add(subsectionEntry)
      LogSwitch.ApplyAddress && Logger.log(states.address, [...registeredSubsections])

      const address = `${states.address}${subsectionEntry}.`

      LogSwitch.ApplyAddress && Logger.log('Registering address', address)
      const unregisterAddr = () => {
        LogSwitch.ApplyAddress && Logger.log('Unregistering address', address)
        registeredSubsections.delete(subsectionEntry)
      }
      return { address, unregisterAddr }
    },
  }), [findRoutesAndDeliver, states.address, registeredSubsections, subscribeFromAddress])

  return (
    <LocalMessagingSectionContext.Provider value={sectionProps}>
      <LocalMessagingContext.Provider value={ctxProps}>
        {children}
      </LocalMessagingContext.Provider>
    </LocalMessagingSectionContext.Provider>
  )
}

export const useLocalMessagingContext = (): LocalMessagingContextProps => {
  return useContext(LocalMessagingContext)
}

